import { TrendingUp } from 'lucide-react'
import { ChartCard } from './ChartCard'
import { FleetValueChart, useFleetIndexedSeries } from './FleetValueChart'
import { useTimeframe } from '../hooks/useTimeframe'
import { timeframeLabel } from '../lib/timeframe'
import type { Book } from '../types'
import './FleetValueOverlay.css'

interface FleetValueOverlayProps {
  books: Book[]
}

/**
 * Fleet-wide indexed-value overlay card: `PairedExcessChart`'s card shell
 * around `FleetValueChart`. The data hook (`useFleetIndexedSeries`) owns the
 * §2.3b base-anchor rule and the §2.4 no-polling contract.
 *
 * Takes the roster as a prop rather than calling useBooks() itself, so it is
 * renderable standalone without stubbing /api/v1/accounts + /engine/status.
 */
export function FleetValueOverlay({ books }: FleetValueOverlayProps) {
  const { timeframe } = useTimeframe()
  const { rows, base, lines, excluded, isLoading, isError } = useFleetIndexedSeries(books)

  const caption =
    base != null ? [`rebased to 100 at ${base}`, ...excluded].join(' · ') : null

  return (
    <ChartCard
      className="fleet-value-overlay"
      title={
        <>
          <TrendingUp size={18} className="icon" aria-hidden="true" />
          Book value · indexed to 100
        </>
      }
      headerExtra={
        <span className="caption">
          {timeframe === 'DAY' ? 'showing 1W · day view' : `window ${timeframeLabel(timeframe)}`}
        </span>
      }
      isLoading={isLoading}
      isError={isError}
      errorText="chart unavailable"
      isEmpty={rows.length < 2}
      emptyText="no comparable history across books yet"
      emptyIcon={<TrendingUp size={28} aria-hidden="true" />}
    >
      <FleetValueChart rows={rows} lines={lines} />
      {caption && <p className="caption fleet-value-overlay-caption">{caption}</p>}
    </ChartCard>
  )
}
